function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { ApolloLink, Observable } from "@apollo/client";
import logHttp from "../../internal/logHttp";
import loadId from "../../internal/shared/loadId";
import { REQUEST_SOURCE_HEADER, getRequestSourceHeader, getRequestSourceInformation } from "../../internal/shared/requestSource";
import { EXCLUDE_FROM_SENTRY } from "../../internal/shared/telemetry/observability";
import { getOperationType } from "../../internal/shared/utils/gql";
const errorType = {
    expected: 'expected',
    muted: 'muted',
    unexpected: 'unexpected'
};
const networkErrorExtensions = {
    actionId: null,
    traceId: null,
    error: {
        code: 'network_error',
        source: 'network',
        status: -1
    },
    unstable_requestId: null
};
function now() {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
export function getOperationInformation(operation) {
    const context = operation.getContext();
    return getRequestSourceInformation({
        service: context.service,
        project: context.queryProject,
        component: context.component,
        operation: operation.operationName
    });
}
function getType(context, code) {
    const { expectedErrors = [], mutedErrors = [] } = context || {};
    if (expectedErrors.includes(code)) {
        return errorType.expected;
    }
    if (mutedErrors.includes(code)) {
        return errorType.muted;
    }
    return errorType.unexpected;
}
function getColor(context, isRead) {
    if (!isRead) {
        return 'primary-light';
    }
    if (context.isPrefetchMode && !context.isDeduplicated) {
        return 'secondary-light';
    }
    if (context.isDeduplicated) {
        return 'primary-light';
    }
    if (context.isPrefetchHit) {
        return 'tertiary';
    }
    return 'error';
}
function getTooltipText(context, isRead) {
    if (!isRead) {
        return 'Mutation hits the network';
    }
    if (context.isPrefetchMode && !context.isDeduplicated) {
        return `Issuing a network request for a prefetched query`;
    }
    if (context.isDeduplicated) {
        return `Not issuing a network request as there was already a request in flight`;
    }
    if (context.isPrefetchHit) {
        return `Retrieving result from prefetch cache (no network request)`;
    }
    return 'This query was not prefetched. Consider adding it to prefetch config.';
}
function shouldMarkPerformance(operationName) {
    try {
        const ls = typeof window !== 'undefined' && window.localStorage;
        if (!ls) {
            return true;
        }
        const debugConfigItem = ls.getItem('DEBUG_SAIL_DATA');
        if (!debugConfigItem) {
            return true;
        }
        const debugConfigValue = JSON.parse(debugConfigItem);
        if (Array.isArray(debugConfigValue)) {
            return debugConfigValue.includes(operationName);
        }
        return true;
    } catch (e) {
        return false;
    }
}
function markPerformance(operationId, operation) {
    if (typeof performance === 'undefined' || !performance.measure) {
        return;
    }
    if (!shouldMarkPerformance(operation.operationName)) {
        return;
    }
    const context = operation.getContext();
    const variables = operation.variables;
    const operationName = operation.operationName;
    const isRead = getOperationType(operation.query) !== 'mutation';
    const isDeduplicated = !!context.isDeduplicated;
    const isPrefetchMode = !!context.isPrefetchMode;
    const isPrefetchHit = !!context.isPrefetchHit;
    const color = getColor(context, isRead);
    try {
        performance.measure(operationName, {
            start: `${operationId}-start`,
            detail: {
                devtools: {
                    dataType: 'track-entry',
                    track: 'Apollo Links',
                    trackGroup: '@sail/data',
                    color,
                    properties: [
                        [
                            'isPrefetchMode',
                            isPrefetchMode
                        ],
                        [
                            'isPrefetchHit',
                            isPrefetchHit
                        ],
                        [
                            'isDeduplicated',
                            isDeduplicated
                        ],
                        [
                            'variables',
                            JSON.stringify(variables, (_key, value)=>value === undefined ? '__UNDEFINED__' : value)
                        ]
                    ],
                    tooltipText: getTooltipText(context, isRead)
                }
            }
        });
    } catch (e) {
    // Ignore error, as 100% of them in the last week have come from very old browsers
    // that don't support the options object passed as second argument to this function.
    // See https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#browser_compatibility
    // for more details.
    }
}
function processSuccess(operation, duration, operationId) {
    var _context_observabilityFns, // TODO: This should also use tags, but we need to remove SDK-PARAMS before.
    _context_observabilityFns1;
    markPerformance(operationId, operation);
    const context = operation.getContext();
    const { service, project, component } = getOperationInformation(operation);
    var _context_queryTag;
    const tags = {
        service,
        project,
        component,
        operation_name: operation.operationName,
        operation_tag: (_context_queryTag = context.queryTag) !== null && _context_queryTag !== void 0 ? _context_queryTag : '<unknown>',
        operation_deduplicate: !!context.isDeduplicated,
        operation_prefetch: !!context.isPrefetchMode,
        operation_prefetch_hit: !!context.isPrefetchHit
    };
    (_context_observabilityFns = context.observabilityFns) === null || _context_observabilityFns === void 0 ? void 0 : _context_observabilityFns.metrics.increment('frontend.data.success', tags);
    var _context_queryTag1, _context_isDeduplicated;
    (_context_observabilityFns1 = context.observabilityFns) === null || _context_observabilityFns1 === void 0 ? void 0 : _context_observabilityFns1.analytics.track('frontend.data.success', {
        // Ownership information:
        service,
        project,
        component,
        // Operation information:
        operation_name: operation.operationName,
        operation_tag: (_context_queryTag1 = context.queryTag) !== null && _context_queryTag1 !== void 0 ? _context_queryTag1 : '<unknown>',
        operation_deduplicate: !!context.isDeduplicated,
        operation_prefetch: !!context.isPrefetchMode,
        operation_prefetch_hit: !!context.isPrefetchHit,
        // TODO(mjimenez): SDK-PARAMS (remove)
        operationName: operation.operationName,
        // TODO(mjimenez): SDK-PARAMS (remove)
        operationDeduplicated: (_context_isDeduplicated = context.isDeduplicated) !== null && _context_isDeduplicated !== void 0 ? _context_isDeduplicated : null,
        // TODO(mjimenez): SDK-PARAMS (remove)
        operationPrefetch: !!context.isPrefetchMode,
        // Network information:
        network_duration: duration !== null && duration !== void 0 ? duration : null,
        network_load_id: loadId,
        // TODO(mjimenez): SDK-PARAMS (remove)
        networkDuration: duration !== null && duration !== void 0 ? duration : null
    });
    const toolkitTags = {
        service,
        project,
        component,
        operation_name: operation.operationName,
        operation_prefetch: !!context.isPrefetchMode,
        operation_prefetch_hit: !!context.isPrefetchHit,
        operation_deduplicate: !!context.isDeduplicated,
        network_duration: duration !== null && duration !== void 0 ? duration : null,
        variables: JSON.stringify(operation.variables)
    };
    sendEventDataToToolkit(toolkitTags, 'success');
}
function processError(operation, duration, error, operationId) {
    markPerformance(operationId, operation);
    const context = operation.getContext();
    const { service, project, component } = getOperationInformation(operation);
    if (context.observabilityFns) {
        var _error_extensions_error, _error_extensions, _error_extensions_error1, _error_extensions1, _error_extensions_error2, _error_extensions2, _error_extensions3, _error_extensions4, _error_extensions5;
        const code = ((_error_extensions = error.extensions) === null || _error_extensions === void 0 ? void 0 : (_error_extensions_error = _error_extensions.error) === null || _error_extensions_error === void 0 ? void 0 : _error_extensions_error.code) || 'could_not_get_error_code';
        const type = getType(context, code);
        var _error_extensions_error_source, _context_queryTag, _error_extensions_error_status, _error_extensions_actionId, _error_extensions_unstable_requestId, _error_extensions_traceId;
        const tags = {
            // Ownership information:
            service,
            project,
            component,
            // Error information:
            errorCode: code,
            errorMessage: error.message || 'No message provided',
            errorPath: (error.path || []).join('.'),
            errorExpected: type === errorType.expected,
            errorMuted: type === errorType.muted,
            errorSource: (_error_extensions_error_source = (_error_extensions1 = error.extensions) === null || _error_extensions1 === void 0 ? void 0 : (_error_extensions_error1 = _error_extensions1.error) === null || _error_extensions_error1 === void 0 ? void 0 : _error_extensions_error1.source) !== null && _error_extensions_error_source !== void 0 ? _error_extensions_error_source : '<unknown>',
            // Operation information:
            operationTag: (_context_queryTag = context.queryTag) !== null && _context_queryTag !== void 0 ? _context_queryTag : '<unknown>',
            operationName: operation.operationName,
            operationPrefetch: !!context.isPrefetchMode,
            operationPrefetchHit: !!context.isPrefetchHit,
            operationDeduplicate: !!context.isDeduplicated,
            // Network information:
            networkDuration: duration !== null && duration !== void 0 ? duration : null,
            networkStatus: (_error_extensions_error_status = (_error_extensions2 = error.extensions) === null || _error_extensions2 === void 0 ? void 0 : (_error_extensions_error2 = _error_extensions2.error) === null || _error_extensions_error2 === void 0 ? void 0 : _error_extensions_error2.status) !== null && _error_extensions_error_status !== void 0 ? _error_extensions_error_status : null,
            networkActionId: (_error_extensions_actionId = (_error_extensions3 = error.extensions) === null || _error_extensions3 === void 0 ? void 0 : _error_extensions3.actionId) !== null && _error_extensions_actionId !== void 0 ? _error_extensions_actionId : null,
            networkRequestId: (_error_extensions_unstable_requestId = (_error_extensions4 = error.extensions) === null || _error_extensions4 === void 0 ? void 0 : _error_extensions4.unstable_requestId) !== null && _error_extensions_unstable_requestId !== void 0 ? _error_extensions_unstable_requestId : null,
            networkTraceId: (_error_extensions_traceId = (_error_extensions5 = error.extensions) === null || _error_extensions5 === void 0 ? void 0 : _error_extensions5.traceId) !== null && _error_extensions_traceId !== void 0 ? _error_extensions_traceId : null
        };
        logHttp('GraphQL', context.observabilityFns, tags);
        const toolkitTags = {
            service,
            project,
            component,
            error_expected: tags.errorExpected,
            error_muted: tags.errorMuted,
            error_source: tags.errorSource,
            operation_name: tags.operationName,
            operation_prefetch: tags.operationPrefetch,
            operation_prefetch_hit: tags.operationPrefetchHit,
            operation_deduplicate: tags.operationDeduplicate,
            network_duration: tags.networkDuration,
            variables: JSON.stringify(operation.variables)
        };
        sendEventDataToToolkit(toolkitTags, 'error');
    }
}
// a temporary function to detect unholy operation inputs for the current client proxy stack
// TODO(victorg): will delete after the v1 graphql gateway project
function monitorUnhollyInputs(operation) {
    var _context_observabilityFns_errors, _context_observabilityFns;
    const context = operation.getContext();
    const { service, project, component } = getOperationInformation(operation);
    const sacrilegiousInputKeys = [
        'include_only',
        'expand',
        'include'
    ];
    const topLevelKeys = operation.variables.input ? new Set(Object.keys(operation.variables.input)) : new Set(Object.keys(operation.variables));
    const intersection = [
        ...sacrilegiousInputKeys
    ].filter((x)=>topLevelKeys.has(x));
    const warning = (_context_observabilityFns = context.observabilityFns) === null || _context_observabilityFns === void 0 ? void 0 : (_context_observabilityFns_errors = _context_observabilityFns.errors) === null || _context_observabilityFns_errors === void 0 ? void 0 : _context_observabilityFns_errors.warning;
    if (Math.random() < 0.001 && intersection.length > 0 && warning) {
        var _context_queryTag;
        const tags = _object_spread_props(_object_spread({
            service,
            project,
            component,
            // Operation information:
            operationTag: (_context_queryTag = context.queryTag) !== null && _context_queryTag !== void 0 ? _context_queryTag : '<unknown>',
            operationName: operation.operationName,
            operationPrefetch: !!context.isPrefetchMode,
            operationPrefetchHit: !!context.isPrefetchHit,
            operationDeduplicate: !!context.isDeduplicated
        }, intersection.reduce((acc, key)=>{
            acc[key] = true;
            return acc;
        }, {})), {
            date: new Date().toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                timeZone: 'UTC'
            })
        });
        warning(`vg|yt: Unholy inputs detected: ${operation.operationName}`, {
            project: 'sail_core',
            extras: tags
        });
    }
}
function sendEventDataToToolkit(report, type) {
    if (typeof window === 'undefined' || !window.SAIL_CORE_DEV_TOOLS_PRESENT) {
        return;
    }
    window.postMessage({
        source: 'webpage',
        type: 'sail_data_observability_event',
        data: _object_spread_props(_object_spread({}, report), {
            type
        })
    }, '*');
}
/**
 * @see https://sail.stripe.me/data/api/createObservabilityLink
 */ export default function createObservabilityLink() {
    let count = 0;
    return new ApolloLink(function observabilityLink(operation, forward) {
        const tmpCount = count;
        count += 1;
        const context = operation.getContext();
        const { headers } = context;
        const info = getOperationInformation(operation);
        const xRequestSource = getRequestSourceHeader(info);
        const operationId = `${operation.operationName}-${tmpCount}`;
        operation.setContext({
            headers: _object_spread({
                [REQUEST_SOURCE_HEADER]: xRequestSource
            }, headers)
        });
        monitorUnhollyInputs(operation);
        return new Observable(function observabilityObserver(observer) {
            const start = now();
            typeof performance !== 'undefined' && performance.mark && performance.mark(`${operationId}-start`);
            const subscription = forward(operation).subscribe({
                next: function observabilityLinkNext(result) {
                    const duration = Math.round(now() - start);
                    if (result.errors) {
                        result.errors.forEach((err)=>{
                            if (typeof err === 'object') {
                                err[EXCLUDE_FROM_SENTRY] = true;
                            }
                            processError(operation, duration, err, operationId);
                        });
                    } else {
                        processSuccess(operation, duration, operationId);
                    }
                    return observer.next(result);
                },
                error: function observabilityLinkError(_error) {
                    const error = sanitizeError(_error);
                    const duration = Math.round(now() - start);
                    processError(operation, duration, {
                        message: error.message,
                        locations: [],
                        path: [
                            '_network_'
                        ],
                        extensions: networkErrorExtensions
                    }, operationId);
                    // Flag the error object as handled by us.
                    error[EXCLUDE_FROM_SENTRY] = true;
                    return observer.error(error);
                },
                complete: function observabilityLinkComplete() {
                    return observer.complete();
                }
            });
            return function observabilityLinkUnsubscribe() {
                if (subscription) {
                    subscription.unsubscribe();
                }
            };
        });
    });
}
function sanitizeError(error) {
    if (error instanceof Error) {
        return error;
    }
    if (typeof error === 'string') {
        return new Error(error);
    }
    if (error && 'message' in error) {
        return new Error(String(error.message));
    }
    return new Error(`Unknown error caught by observability link. ${error}`);
}

//# sourceMappingURL=createObservabilityLink.js.map